/**
 * Root domains (outside `*.mijnpraktijk.nl`) that can serve a care group's
 * public landing page directly under a subdomain of their own, keeping that
 * hostname in the visitor's address bar (see `CareGroup.externalDomains` and
 * `infra/src/components/frontendExternalDomain.ts`). Add a domain here to
 * extend where a care group's landing page may be reached from, once its
 * Route53 hosted zone and ACM certificate(s) are provisioned in AWS (see the
 * infra component's docs) — `infra` keeps its own copy of this list since it
 * doesn't depend on this workspace, so update both.
 */
export const EXTERNAL_DOMAINS = ["iedereenhuisarts.nl", "zoekeenhuisartspraktijk.nl"] as const

export type ExternalDomain = (typeof EXTERNAL_DOMAINS)[number]

/**
 * The non-production environments that serve `EXTERNAL_DOMAINS` under their
 * own subdomain prefix, mirroring the `*.mijnpraktijk.nl` convention (e.g.
 * `dev.mijnpraktijk.nl` / `dev.iedereenhuisarts.nl`). Production has no
 * prefix — a bare `iedereenhuisarts.nl` subdomain.
 */
export type ExternalDomainEnvironment = "development" | "staging" | "production"

export const EXTERNAL_DOMAIN_ENVIRONMENTS: ExternalDomainEnvironment[] = ["development", "staging", "production"]

const ENVIRONMENT_PREFIXES: Record<ExternalDomainEnvironment, string> = {
  development: "dev.",
  staging: "staging.",
  production: "",
}

/**
 * The hostname suffixes `EXTERNAL_DOMAINS` are reachable under for
 * `environment`, e.g. for `"development"`: `["dev.iedereenhuisarts.nl",
 * "dev.zoekeenhuisartspraktijk.nl"]`. A hostname matches a suffix when it
 * equals it exactly or is a `.`-delimited subdomain of it.
 */
export const externalDomainSuffixes = (environment: ExternalDomainEnvironment): string[] =>
  EXTERNAL_DOMAINS.map((domain) => `${ENVIRONMENT_PREFIXES[environment]}${domain}`)

/**
 * Every hostname suffix `EXTERNAL_DOMAINS` are reachable under, across all
 * environments (see {@link externalDomainSuffixes}) — e.g. both
 * `iedereenhuisarts.nl` and `dev.iedereenhuisarts.nl`. Used by
 * `ExternalDomainHostname` to restrict which hostnames a care group may
 * register: a hostname must equal one of these suffixes or be a
 * `.`-delimited subdomain of one.
 */
export const EXTERNAL_DOMAIN_SUFFIXES: string[] = EXTERNAL_DOMAIN_ENVIRONMENTS.flatMap((environment) =>
  externalDomainSuffixes(environment)
)

/**
 * Whether `hostname` is `suffix` itself, or a `.`-delimited subdomain of it.
 * A plain `endsWith` would let e.g. `notdev.mijnpraktijk.nl` be mistaken for
 * a subdomain of `dev.mijnpraktijk.nl`. Both are expected pre-normalized
 * (trimmed, lowercased).
 */
export const matchesDomainSuffix = (hostname: string, suffix: string): boolean =>
  hostname === suffix || hostname.endsWith(`.${suffix}`)
